import {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ProductType } from "@b2bportal/core-types";
import {
  groundApi,
  type GroundCancelFulfillPollResponseAborted,
  GroundCancelFulfillPollResponseEnum,
  GroundCancelFulfillScheduleResponseEnum,
  type GroundCancelScenario,
  GroundCancelScenarioEnum,
  GroundCancelQuoteScheduleResponseEnum,
  type Itinerary,
} from "@b2bportal/ground-api";
import {
  rbcGroundCancelV2Api,
  type GroundCancelFulfillPollAborted,
  type GroundCancelScenarioV2,
  GroundCancelFulfillPollResponseV2Enum,
  GroundCancelFulfillScheduleResponseV2Enum,
  GroundCancelQuotePollResponseV2Enum,
  GroundCancelQuoteScheduleResponseV2Enum,
  GroundCancelScenarioV2Enum,
} from "@b2bportal/rbc-ground-cancel-v2-api";
import { trackEvent } from "@hopper-b2b/api";
import { CancelModalFlowStepEnum, SelfServeEvents } from "@hopper-b2b/types";
import axios from "axios";
import { adaptV1ScenarioToV2 } from "./adaptV1ScenarioToV2";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import { SELF_SERVE_CANCEL_POLL_INTERVAL_MS } from "@/utilities";

const groundCancelV2Api = rbcGroundCancelV2Api(axios);

// Fulfillment rejects with an Error, a v1 Aborted enum string, or a v2 failureType object.
const toCancelFailureReason = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (
    typeof err === "object" &&
    err !== null &&
    "GroundCancelFulfillFailureType" in err
  ) {
    return String(err.GroundCancelFulfillFailureType);
  }
  return "unknown";
};

export interface ICancelGroundModalContentProps {
  itinerary: Itinerary;
  onClose?: () => void;
  onFulfillSuccess?: () => void;
  // Return an element to render if the error was handled so we can call resolve instead of calling reject
  onFulfillError?: (
    error:
      | GroundCancelFulfillPollResponseAborted
      | GroundCancelFulfillPollAborted,
    onClose?: () => void
  ) => ReactElement | undefined;
}

export const useCarCancelPolicy = (props: ICancelGroundModalContentProps) => {
  const { itinerary, onClose, onFulfillSuccess, onFulfillError } = props;
  const [cancelScenario, setCancelScenario] =
    useState<GroundCancelScenarioV2 | null>(null);
  const { internalGroundBookingId: groundBookingId } =
    itinerary?.reservation || {};
  const [cancelFlowStep, setCancelFlowStep] = useState<
    CancelModalFlowStepEnum | undefined
  >();
  const pollIdRef = useRef<number>();
  // v2 path: ciphered handle from the quote used to start fulfillment
  const [fulfillmentHandle, setFulfillmentHandle] = useState<string | null>(
    null
  );
  // v1 path: session id used to start fulfillment
  const [quoteSessionId, setQuoteSessionId] = useState<string | null>(null);

  const { isExperimentVariant } = useExperiment();
  const isV2Enabled = isExperimentVariant(
    Experiments.GROUND_CANCEL_V2_ENABLED.name,
    Experiments.GROUND_CANCEL_V2_ENABLED.variants.available
  );

  const goToConfirm = useCallback(() => {
    trackEvent({
      eventName: SelfServeEvents.ClickCancel,
      properties: { product: ProductType.CARS },
    });
    setCancelFlowStep(CancelModalFlowStepEnum.confirm);
  }, []);

  const clearPollInterval = useCallback(() => {
    const { current: pollId } = pollIdRef;

    if (pollId) {
      window.clearInterval(pollId);

      pollIdRef.current = 0;
    }
  }, [pollIdRef]);

  useEffect(() => clearPollInterval, [clearPollInterval]);

  const getCancelQuote = useCallback(async () => {
    if (isV2Enabled) {
      const scheduleResp =
        await groundCancelV2Api.apiV2SelfserveGroundCancelQuoteSchedulePost({
          reservationId: groundBookingId,
        });
      const scheduleData = scheduleResp.data;

      // Quote already computed at schedule time — no polling needed
      if (
        scheduleData.GroundCancelQuoteScheduleResponseV2 ===
        GroundCancelQuoteScheduleResponseV2Enum.Success
      ) {
        return scheduleData.quote;
      }

      if (
        scheduleData.GroundCancelQuoteScheduleResponseV2 ===
        GroundCancelQuoteScheduleResponseV2Enum.Failure
      ) {
        setCancelFlowStep(CancelModalFlowStepEnum.error);
        throw new Error("getCancelQuote: schedule returned Failure");
      }

      // Pending — poll until success or failure
      const sessionId = (
        scheduleData as {
          quoteSessionId?: string;
          GroundCancelQuoteScheduleResponseV2: string;
        }
      ).quoteSessionId;
      if (!sessionId) {
        setCancelFlowStep(CancelModalFlowStepEnum.error);
        throw new Error(
          "getCancelQuote: missing quoteSessionId in Pending response"
        );
      }

      return new Promise<
        (typeof scheduleData & { quote: NonNullable<unknown> })["quote"]
      >((resolve, reject) => {
        pollIdRef.current = window.setInterval(async () => {
          try {
            const pollResp =
              await groundCancelV2Api.apiV2SelfserveGroundCancelQuotePollPut({
                quoteSessionId: sessionId,
              });
            const pollData = pollResp.data;

            if (
              pollData.GroundCancelQuotePollResponseV2 ===
              GroundCancelQuotePollResponseV2Enum.Success
            ) {
              clearPollInterval();
              resolve(pollData.quote);
            } else if (
              pollData.GroundCancelQuotePollResponseV2 ===
              GroundCancelQuotePollResponseV2Enum.Failure
            ) {
              clearPollInterval();
              reject(new Error("getCancelQuote: poll returned Failure"));
            }
            // Pending — keep polling
          } catch (err) {
            clearPollInterval();
            reject(err);
          }
        }, SELF_SERVE_CANCEL_POLL_INTERVAL_MS);
      });
    }

    // v1 path
    const resp = await groundApi(
      axios
    ).apiV1SelfserveGroundCancelQuoteSchedulePost({
      reservationId: groundBookingId,
    });
    if (
      resp.data.GroundCancelQuoteScheduleResponse ===
      GroundCancelQuoteScheduleResponseEnum.Success
    ) {
      const id = resp.data.quoteSessionId;
      setQuoteSessionId(id);
      return new Promise<GroundCancelScenario>((resolve, reject) => {
        pollIdRef.current = window.setInterval(async () => {
          try {
            const res = await groundApi(
              axios
            ).apiV1SelfserveGroundCancelQuotePollPut({
              quoteSessionId: id,
            });

            if (
              res.data.GroundCancelQuotePollResponse ===
              GroundCancelFulfillScheduleResponseEnum.Success
            ) {
              clearPollInterval();
              resolve(res.data.cancelScenario);
            }
          } catch (err) {
            clearPollInterval();
            reject(err);
          }
        }, SELF_SERVE_CANCEL_POLL_INTERVAL_MS);
      });
    } else {
      setCancelFlowStep(CancelModalFlowStepEnum.error);
    }
  }, [clearPollInterval, groundBookingId, isV2Enabled]);

  // Returns true on success, false when a tenant error was handled (flow step already set).
  // Rejects on unhandled error.
  const getCancelFulfill = useCallback(async (): Promise<boolean> => {
    if (isV2Enabled) {
      if (!fulfillmentHandle) {
        setCancelFlowStep(CancelModalFlowStepEnum.error);
        throw new Error("getCancelFulfill: missing fulfillmentHandle");
      }

      const scheduleResp =
        await groundCancelV2Api.apiV2SelfserveGroundCancelFulfillSchedulePost({
          fulfillmentHandle,
        });
      const scheduleData = scheduleResp.data;

      if (
        scheduleData.GroundCancelFulfillScheduleResponseV2 ===
        GroundCancelFulfillScheduleResponseV2Enum.Failure
      ) {
        setCancelFlowStep(CancelModalFlowStepEnum.error);
        throw new Error("getCancelFulfill: schedule returned Failure");
      }

      const fulfillmentSessionId = (
        scheduleData as {
          fulfillmentSessionId?: string;
          GroundCancelFulfillScheduleResponseV2: string;
        }
      ).fulfillmentSessionId;
      if (!fulfillmentSessionId) {
        setCancelFlowStep(CancelModalFlowStepEnum.error);
        throw new Error(
          "getCancelFulfill: missing fulfillmentSessionId in schedule response"
        );
      }

      return new Promise((resolve, reject) => {
        pollIdRef.current = window.setInterval(async () => {
          try {
            const pollResp =
              await groundCancelV2Api.apiV2SelfserveGroundCancelFulfillPollPut({
                fulfillmentSessionId,
              });
            const pollData = pollResp.data;

            if (
              pollData.GroundCancelFulfillPollResponseV2 ===
              GroundCancelFulfillPollResponseV2Enum.Success
            ) {
              clearPollInterval();
              setFulfillmentHandle(null);
              resolve(true);
            } else if (
              pollData.GroundCancelFulfillPollResponseV2 ===
              GroundCancelFulfillPollResponseV2Enum.Aborted
            ) {
              const abortedData = pollData as GroundCancelFulfillPollAborted;
              const tenantError = onFulfillError?.(abortedData, onClose);
              clearPollInterval();
              setFulfillmentHandle(null);
              if (tenantError == null) {
                setCancelFlowStep(CancelModalFlowStepEnum.error);
                reject(abortedData.failureType);
              } else {
                setCancelFlowStep(CancelModalFlowStepEnum.tenantError);
                resolve(false);
              }
            } else if (
              pollData.GroundCancelFulfillPollResponseV2 ===
              GroundCancelFulfillPollResponseV2Enum.Failed
            ) {
              clearPollInterval();
              setFulfillmentHandle(null);
              setCancelFlowStep(CancelModalFlowStepEnum.error);
              reject(new Error("getCancelFulfill: fulfill returned Failed"));
            }
            // Pending — keep polling
          } catch (err) {
            clearPollInterval();
            setFulfillmentHandle(null);
            reject(err);
          }
        }, SELF_SERVE_CANCEL_POLL_INTERVAL_MS);
      });
    }

    // v1 path
    const resp = await groundApi(
      axios
    ).apiV1SelfserveGroundCancelFulfillSchedulePost({
      quoteSessionId,
    });
    if (
      resp.data.GroundCancelFulfillScheduleResponse ===
      GroundCancelFulfillScheduleResponseEnum.Success
    ) {
      const fulfillmentSessionId = resp.data.fulfillmentSessionId;
      return new Promise((resolve, reject) => {
        pollIdRef.current = window.setInterval(async () => {
          try {
            const res = await groundApi(
              axios
            ).apiV1SelfserveGroundCancelFulfillPollPut({
              fulfillmentSessionId,
            });

            if (
              res.data.GroundCancelFulfillPollResponse ===
              GroundCancelFulfillPollResponseEnum.Success
            ) {
              clearPollInterval();
              setQuoteSessionId(null);
              resolve(true);
            } else if (
              res.data.GroundCancelFulfillPollResponse ===
              GroundCancelFulfillPollResponseEnum.Aborted
            ) {
              const failureReason =
                res.data as GroundCancelFulfillPollResponseAborted;
              const tenantError = onFulfillError?.(failureReason, onClose);
              clearPollInterval();
              setQuoteSessionId(null);
              if (tenantError == null) {
                setCancelFlowStep(CancelModalFlowStepEnum.error);
                reject(failureReason.Aborted);
              } else {
                setCancelFlowStep(CancelModalFlowStepEnum.tenantError);
                resolve(false);
              }
            }
          } catch (err) {
            setQuoteSessionId(null);
            clearPollInterval();
            reject(err);
          }
        }, SELF_SERVE_CANCEL_POLL_INTERVAL_MS);
      });
    } else {
      setCancelFlowStep(CancelModalFlowStepEnum.error);
      return false;
    }
  }, [
    clearPollInterval,
    fulfillmentHandle,
    isV2Enabled,
    onClose,
    onFulfillError,
    quoteSessionId,
  ]);

  const confirmCancellation = useCallback(() => {
    setCancelFlowStep(CancelModalFlowStepEnum.loading);
    // Two-argument then: a throw in the success branch must not also track a failure.
    getCancelFulfill().then(
      (succeeded) => {
        if (succeeded) {
          trackEvent({
            eventName: SelfServeEvents.ResponseSuccess,
            properties: { product: ProductType.CARS },
          });
          onFulfillSuccess?.();
          setCancelFlowStep(CancelModalFlowStepEnum.success);
        } else {
          // Flow step was already set by getCancelFulfill (tenantError, or error on the v1 schedule path)
          trackEvent({
            eventName: SelfServeEvents.ResponseFailure,
            properties: { product: ProductType.CARS },
          });
        }
      },
      (err: unknown) => {
        trackEvent({
          eventName: SelfServeEvents.ResponseFailure,
          properties: {
            product: ProductType.CARS,
            reason: toCancelFailureReason(err),
          },
        });
        setCancelFlowStep(CancelModalFlowStepEnum.tryAgain);
      }
    );
  }, [getCancelFulfill, onFulfillSuccess]);

  const getPolicyInfo = useCallback(() => {
    // Drop any previous quote so a retry can't fulfil against stale data and
    // so the loading state reads as "loading details", not "processing".
    setCancelScenario(null);
    setFulfillmentHandle(null);
    setQuoteSessionId(null);
    setCancelFlowStep(CancelModalFlowStepEnum.loading);

    getCancelQuote()
      .then((result) => {
        if (!result) return;

        if (isV2Enabled) {
          const v2Quote = result as {
            cancelScenario: GroundCancelScenarioV2;
            fulfillmentHandle?: string;
          };
          setCancelScenario(v2Quote.cancelScenario);

          const scenarioKey = v2Quote.cancelScenario.GroundCancelScenarioV2;
          const isCancellable =
            scenarioKey === GroundCancelScenarioV2Enum.FullyRefundable ||
            scenarioKey ===
              GroundCancelScenarioV2Enum.RefundableWithConditions ||
            scenarioKey === GroundCancelScenarioV2Enum.NonRefundable;

          if (isCancellable && !v2Quote.fulfillmentHandle) {
            // Cancellable scenario with no handle — BFF inconsistency, surface an error
            return setCancelFlowStep(CancelModalFlowStepEnum.error);
          }

          if (v2Quote.fulfillmentHandle) {
            setFulfillmentHandle(v2Quote.fulfillmentHandle);
          }

          switch (scenarioKey) {
            case GroundCancelScenarioV2Enum.ContactCustomerService:
              return setCancelFlowStep(CancelModalFlowStepEnum.support);
            case GroundCancelScenarioV2Enum.FullyRefundable:
              return setCancelFlowStep(CancelModalFlowStepEnum.policy);
            case GroundCancelScenarioV2Enum.RefundableWithConditions:
              return setCancelFlowStep(CancelModalFlowStepEnum.policy);
            case GroundCancelScenarioV2Enum.Canceled:
              return setCancelFlowStep(CancelModalFlowStepEnum.support);
            case GroundCancelScenarioV2Enum.NonRefundable:
              return setCancelFlowStep(CancelModalFlowStepEnum.nonRefundable);
            case GroundCancelScenarioV2Enum.BookingPending:
              return setCancelFlowStep(CancelModalFlowStepEnum.support);
            case GroundCancelScenarioV2Enum.PickupInThePast:
              return setCancelFlowStep(CancelModalFlowStepEnum.support);
            case GroundCancelScenarioV2Enum.RetriableFailure:
              return setCancelFlowStep(CancelModalFlowStepEnum.tryAgain);
          }
        } else {
          const v1Scenario = result as GroundCancelScenario;
          setCancelScenario(adaptV1ScenarioToV2(v1Scenario));
          switch (v1Scenario.GroundCancelScenario) {
            case GroundCancelScenarioEnum.ContactCustomerService:
              return setCancelFlowStep(CancelModalFlowStepEnum.support);
            case GroundCancelScenarioEnum.FullyRefundable:
              return setCancelFlowStep(CancelModalFlowStepEnum.policy);
            case GroundCancelScenarioEnum.Canceled:
              return setCancelFlowStep(CancelModalFlowStepEnum.support);
            case GroundCancelScenarioEnum.NonRefundable:
              return setCancelFlowStep(CancelModalFlowStepEnum.nonRefundable);
            case GroundCancelScenarioEnum.Pending:
              return setCancelFlowStep(CancelModalFlowStepEnum.loading);
          }
        }
      })
      .catch(() => {
        setCancelFlowStep(CancelModalFlowStepEnum.tryAgain);
      });
  }, [getCancelQuote, isV2Enabled]);

  const handleGroundCancel = useCallback(() => {
    // Only fulfil against a loaded quote. Without one there is nothing to
    // cancel, and moving to processingCancellation would leave the page blank.
    if (!cancelScenario || !groundBookingId) {
      return;
    }

    trackEvent({
      eventName: SelfServeEvents.ConfirmCancelation,
      properties: { product: ProductType.CARS },
    });

    setCancelFlowStep(CancelModalFlowStepEnum.processingCancellation);
    confirmCancellation();
  }, [cancelScenario, confirmCancellation, groundBookingId]);

  return {
    isQuoteLoading: cancelFlowStep === CancelModalFlowStepEnum.loading,
    isFulfilling:
      cancelScenario != null &&
      cancelFlowStep === CancelModalFlowStepEnum.loading,
    cancelFlowStep,
    getCancelQuote,
    getPolicyInfo,
    handleGroundCancel,
    goToConfirm,
    cancelScenario,
  };
};
